import {
  Stack,
  Typography,
  Checkbox,
  Paper,
  Box,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  useEffect, useState, useMemo, memo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InfoChip } from '../../components/CustomChip';
import { SearchBar } from '../../components/FormComponents';
import { CustomDialogBox } from '../../components/CustomDialogBox';
import { getFeaturesList } from '../../features';
import useDebouncedValue from '../../hooks/useDebouncedValue';

const FeatureItem = memo(({
  id, label, checked, onToggle,
}) => (
  <Paper
    onClick={() => onToggle(id)}
    sx={{
      px: 2,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      cursor: 'pointer',
      width: '100%',
      backgroundColor: 'grey.100',
      color: checked ? 'primary.main' : 'initial',
    }}
  >
    <Checkbox
      size="small"
      checked={checked}
      onChange={() => onToggle(id)}
      onClick={(e) => e.stopPropagation()} // avoid double toggle
    />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {label}
      </Typography>
    </Box>
  </Paper>
));

export default function FeatureDialog({
  open,
  onClose,
  selectedFeatures,
  setSelectedFeatures,
  onSave,
}) {
  const dispatch = useDispatch();
  const featureManagerList = useSelector(
    (state) => state.featureManagerList.featureManagerList,
  );

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  useEffect(() => {
    dispatch(getFeaturesList());
  }, [dispatch]);

  const toggleFeature = (featureId) => {
    const exists = selectedFeatures.some((f) => f.id === featureId);
    if (exists) {
      setSelectedFeatures((prev) => prev.filter((f) => f.id !== featureId));
    } else if (selectedFeatures.length < 10) {
      const feature = featureManagerList.find((f) => f.id === featureId);
      if (feature) {
        setSelectedFeatures((prev) => [
          ...prev,
          { id: feature.id, featureName: feature.feature_name },
        ]);
      }
    }
  };

  const filteredList = useMemo(() => {
    if (!debouncedSearch) return featureManagerList;
    const lower = debouncedSearch.toLowerCase();
    return featureManagerList.filter((f) => f.feature_name.toLowerCase().includes(lower));
  }, [featureManagerList, debouncedSearch]);

  return (
    <CustomDialogBox
      title="Select Features"
      open={open}
      handleClose={onClose}
      content={(
        <Stack gap={2}>
          <InfoChip
            icon={<InfoIcon color="primary" />}
            label="You can select up to 10 features for the selected taxonomy node."
          />

          <SearchBar
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm('')}
          />

          <Stack gap={1} sx={{ width: '100%' }}>
            {filteredList.map((feature) => (
              <FeatureItem
                key={feature.id}
                id={feature.id}
                label={feature.feature_name}
                checked={selectedFeatures.some((f) => f.id === feature.id)}
                onToggle={toggleFeature}
              />
            ))}
          </Stack>
        </Stack>
      )}
      actions={{
        title: 'Save',
        onClick: () => onSave(selectedFeatures),
      }}
    />
  );
}
